import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  FileText, Plus, Upload, Download, Trash2, Filter, Search, Eye,
  FolderOpen, File, FileSpreadsheet, FileImage, Shield, Clock,
  Tag, X, ChevronDown, History, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORY_CONFIG = {
  plans: { label: 'Planos', color: '#3B82F6', icon: '📐' },
  permits: { label: 'Permisos', color: '#8B5CF6', icon: '📋' },
  certificates: { label: 'Certificados', color: '#10B981', icon: '🏆' },
  contracts: { label: 'Contratos', color: '#F59E0B', icon: '📝' },
  manuals: { label: 'Manuales', color: '#06B6D4', icon: '📖' },
  reports: { label: 'Informes', color: '#EC4899', icon: '📊' },
  photos: { label: 'Fotos', color: '#EF4444', icon: '📷' },
  general: { label: 'General', color: '#64748B', icon: '📁' },
};

const FILE_ICONS = {
  pdf: { icon: FileText, color: '#EF4444' },
  doc: { icon: FileText, color: '#3B82F6' }, docx: { icon: FileText, color: '#3B82F6' },
  xls: { icon: FileSpreadsheet, color: '#10B981' }, xlsx: { icon: FileSpreadsheet, color: '#10B981' },
  jpg: { icon: FileImage, color: '#F59E0B' }, jpeg: { icon: FileImage, color: '#F59E0B' },
  png: { icon: FileImage, color: '#F59E0B' },
  dwg: { icon: File, color: '#8B5CF6' }, dxf: { icon: File, color: '#8B5CF6' },
};

function getFileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || { icon: File, color: '#64748B' };
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export default function Documents() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showVersions, setShowVersions] = useState(null);
  const fileInputRef = useRef(null);
  const versionInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ name: '', category: 'general', description: '', tags: '' });
  const [uploading, setUploading] = useState(false);

  const canEdit = ['superadmin', 'admin_empresa', 'project_manager', 'supervisor'].includes(user?.role);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadDocuments(); }, [selectedProject, filterCategory]);

  const loadDocuments = () => {
    const params = {};
    if (selectedProject) params.project_id = selectedProject;
    if (filterCategory) params.category = filterCategory;
    api.get('/documents', { params }).then(r => setDocuments(r.data)).catch(() => {});
    if (selectedProject) {
      api.get('/documents/stats/summary', { params: { project_id: selectedProject } })
        .then(r => setStats(r.data)).catch(() => {});
    }
  };

  const loadDocDetail = async (id) => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setSelectedDoc(data);
    } catch (e) { toast.error('Error'); }
  };

  const uploadDocument = async (file) => {
    if (!selectedProject) return toast.error('Selecciona un proyecto');
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', selectedProject);
      formData.append('category', uploadForm.category);
      formData.append('name', uploadForm.name || file.name);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);

      await api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Documento subido');
      setShowUpload(false);
      setUploadForm({ name: '', category: 'general', description: '', tags: '' });
      loadDocuments();
    } catch (e) { toast.error('Error subiendo documento'); }
    finally { setUploading(false); }
  };

  const uploadVersion = async (docId, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/documents/${docId}/version`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Nueva versión subida');
      loadDocuments();
      if (selectedDoc?.id === docId) loadDocDetail(docId);
    } catch (e) { toast.error('Error'); }
    finally { setUploading(false); }
  };

  const approveDocument = async (id) => {
    try {
      await api.put(`/documents/${id}/approve`);
      toast.success('Documento aprobado');
      loadDocuments();
      if (selectedDoc?.id === id) loadDocDetail(id);
    } catch (e) { toast.error('Error'); }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await api.delete(`/documents/${id}`);
      setDocuments(prev => prev.filter(d => d.id !== id));
      if (selectedDoc?.id === id) setSelectedDoc(null);
      toast.success('Eliminado');
    } catch (e) { toast.error('Error'); }
  };

  // Filter by search
  const filtered = documents.filter(d => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return d.name.toLowerCase().includes(term) ||
             d.file_name?.toLowerCase().includes(term) ||
             d.description?.toLowerCase().includes(term);
    }
    return true;
  });

  // Group by category
  const grouped = {};
  filtered.forEach(d => {
    const cat = d.category || 'general';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  return (
    <div className="documents-page">
      <div className="page-header">
        <div>
          <h1><FolderOpen size={24} /> Documentos</h1>
          <p className="subtitle">Control de versiones y documentación del proyecto</p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            <Upload size={16} /> Subir Documento
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="doc-stats">
          <div className="doc-stat"><span className="doc-stat-value">{stats.total || 0}</span><span>Documentos</span></div>
          <div className="doc-stat"><span className="doc-stat-value">{stats.categories || 0}</span><span>Categorías</span></div>
          <div className="doc-stat"><span className="doc-stat-value">{formatSize(stats.total_size)}</span><span>Tamaño total</span></div>
          <div className="doc-stat"><span className="doc-stat-value">{stats.approved || 0}</span><span>Aprobados</span></div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Todos los proyectos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        <select className="filter-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="">Todas las categorías</option>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
        <div className="search-input-wrap">
          <Search size={14} />
          <input className="search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar documento..." />
        </div>
      </div>

      <div className="documents-layout">
        {/* Document List */}
        <div className="documents-list">
          {Object.keys(grouped).length === 0 && !loading && (
            <div className="empty-state"><FolderOpen size={48} /><h3>Sin documentos</h3><p>Sube el primer documento del proyecto</p></div>
          )}
          {Object.entries(grouped).map(([cat, docs]) => {
            const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.general;
            return (
              <div key={cat} className="doc-category-group">
                <div className="doc-category-header">
                  <span>{cfg.icon}</span>
                  <span className="doc-category-name">{cfg.label}</span>
                  <span className="doc-category-count">{docs.length}</span>
                </div>
                {docs.map(doc => {
                  const fi = getFileIcon(doc.file_name);
                  const FileIcon = fi.icon;
                  return (
                    <div key={doc.id}
                      className={`doc-row ${selectedDoc?.id === doc.id ? 'active' : ''}`}
                      onClick={() => loadDocDetail(doc.id)}>
                      <FileIcon size={18} style={{ color: fi.color, flexShrink: 0 }} />
                      <div className="doc-row-info">
                        <span className="doc-row-name">{doc.name}</span>
                        <span className="doc-row-meta">
                          v{doc.version} · {formatSize(doc.file_size)} · {doc.uploaded_by_name}
                          · {new Date(doc.created_at).toLocaleDateString('es')}
                        </span>
                      </div>
                      {doc.approved_by && <Check size={14} style={{ color: '#10B981', flexShrink: 0 }} title="Aprobado" />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Detail Panel */}
        <div className="document-detail">
          {!selectedDoc ? (
            <div className="empty-state"><FileText size={48} /><p>Selecciona un documento</p></div>
          ) : (
            <div className="doc-detail-content">
              <div className="doc-detail-header">
                <div>
                  <h2>{selectedDoc.name}</h2>
                  <div className="doc-detail-meta">
                    <span className="cl-badge" style={{
                      background: (CATEGORY_CONFIG[selectedDoc.category]?.color || '#64748B') + '20',
                      color: CATEGORY_CONFIG[selectedDoc.category]?.color || '#64748B'
                    }}>{CATEGORY_CONFIG[selectedDoc.category]?.label || selectedDoc.category}</span>
                    <span>v{selectedDoc.version}</span>
                    <span>{formatSize(selectedDoc.file_size)}</span>
                    <span>{selectedDoc.project_code}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="doc-actions">
                <a className="btn btn-sm btn-primary" href={selectedDoc.file_url} download target="_blank" rel="noopener noreferrer">
                  <Download size={14} /> Descargar
                </a>
                {canEdit && (
                  <>
                    <button className="btn btn-sm btn-secondary" onClick={() => versionInputRef.current?.click()}>
                      <Upload size={14} /> Nueva versión
                    </button>
                    <input ref={versionInputRef} type="file" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) uploadVersion(selectedDoc.id, e.target.files[0]); e.target.value = ''; }} />
                    {!selectedDoc.approved_by && (
                      <button className="btn btn-sm btn-success" onClick={() => approveDocument(selectedDoc.id)}>
                        <Shield size={14} /> Aprobar
                      </button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteDocument(selectedDoc.id)}>
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>

              {/* Info */}
              <div className="doc-info-grid">
                <div><label>Archivo</label><span className="mono">{selectedDoc.file_name}</span></div>
                <div><label>Tipo</label><span>{selectedDoc.mime_type || '—'}</span></div>
                <div><label>Subido por</label><span>{selectedDoc.uploaded_by_name}</span></div>
                <div><label>Fecha</label><span>{new Date(selectedDoc.created_at).toLocaleString('es')}</span></div>
                {selectedDoc.description && <div style={{ gridColumn: '1/-1' }}><label>Descripción</label><span>{selectedDoc.description}</span></div>}
                {selectedDoc.tags?.length > 0 && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <label>Tags</label>
                    <div className="doc-tags">
                      {selectedDoc.tags.map((t, i) => <span key={i} className="doc-tag"><Tag size={10} /> {t}</span>)}
                    </div>
                  </div>
                )}
              </div>

              {selectedDoc.approved_by_name && (
                <div className="cl-approved-banner" style={{ marginTop: 12 }}>
                  <Shield size={16} /> Aprobado por {selectedDoc.approved_by_name}
                  {selectedDoc.approved_at && ` — ${new Date(selectedDoc.approved_at).toLocaleDateString('es')}`}
                </div>
              )}

              {/* Version History */}
              {selectedDoc.versions?.length > 1 && (
                <div className="doc-versions">
                  <h4><History size={14} /> Historial de versiones ({selectedDoc.versions.length})</h4>
                  <div className="doc-versions-list">
                    {selectedDoc.versions.map(v => (
                      <div key={v.id} className={`doc-version-row ${v.is_current ? 'current' : ''}`}>
                        <span className="doc-v-num">v{v.version}</span>
                        <span className="doc-v-name">{v.file_name}</span>
                        <span className="doc-v-size">{formatSize(v.file_size)}</span>
                        <span className="doc-v-user">{v.uploaded_by_name}</span>
                        <span className="doc-v-date">{new Date(v.created_at).toLocaleDateString('es')}</span>
                        {v.is_current && <span className="doc-v-current">Actual</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="modal-overlay" onClick={() => setShowUpload(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Subir Documento</h3>
              <button className="btn-icon" onClick={() => setShowUpload(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Proyecto *</label>
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Categoría</label>
                <select value={uploadForm.category} onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Nombre</label>
                <input value={uploadForm.name} onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Nombre descriptivo (o se usa el del archivo)" />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea rows={2} value={uploadForm.description}
                  onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descripción opcional..." />
              </div>
              <div className="form-group">
                <label>Tags (separados por coma)</label>
                <input value={uploadForm.tags} onChange={e => setUploadForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="Ej: rev-A, aprobado, final" />
              </div>
              <div className="form-group">
                <label>Archivo *</label>
                <input ref={fileInputRef} type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.dwg,.dxf,.zip,.rar,.jpg,.jpeg,.png,.txt,.csv" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUpload(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={uploading}
                onClick={() => uploadDocument(fileInputRef.current?.files[0])}>
                {uploading ? 'Subiendo...' : 'Subir Documento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
